import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Building, Users, HandshakeIcon, Shield, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[350px] flex items-center justify-center overflow-hidden bg-[#0077B6]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0077B6] to-[#005A8C]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3 text-white">About Utmost Financial Inc.</h1>
          <p className="text-lg text-blue-100">
            Your trusted partner in financial security and growth
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Story</h2>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mb-6"></div>
            <div className="text-gray-600 leading-relaxed space-y-4 text-left text-lg">
              <p>
                Founded in 2021, Utmost Financial Inc. has been serving Canadians with comprehensive financial solutions from our Winnipeg office. We are a team of dedicated professionals committed to helping individuals and families achieve their financial goals through expert guidance in insurance, investments, and mortgage services.
              </p>
              <p>
                At Utmost Financial, we believe that financial security is the foundation of a prosperous life. Our mission is to provide personalized, client-focused service that empowers you to make informed decisions about your financial future. Whether you're protecting your family with insurance, building wealth through investments, or finding the perfect mortgage, we're here to guide you every step of the way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-12 bg-[#F0F7FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">Our Core Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              The principles that guide everything we do
            </p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center bg-white border border-gray-200 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mx-auto mb-3">
                  <Shield className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="font-bold text-gray-800 text-lg mb-2">Integrity</h3>
                <p className="text-gray-600">We uphold the highest ethical standards in all our dealings</p>
              </CardContent>
            </Card>
            <Card className="text-center bg-white border border-gray-200 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 border-2 border-[#D4AF37] flex items-center justify-center mx-auto mb-3">
                  <Users className="h-7 w-7 text-[#D4AF37]" />
                </div>
                <h3 className="font-bold text-gray-800 text-lg mb-2">Client Focus</h3>
                <p className="text-gray-600">Your goals and needs are at the center of everything we do</p>
              </CardContent>
            </Card>
            <Card className="text-center bg-white border border-gray-200 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mx-auto mb-3">
                  <HandshakeIcon className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="font-bold text-gray-800 text-lg mb-2">Trust</h3>
                <p className="text-gray-600">Building long-term relationships based on transparency</p>
              </CardContent>
            </Card>
            <Card className="text-center bg-white border border-gray-200 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 border-2 border-[#D4AF37] flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="h-7 w-7 text-[#D4AF37]" />
                </div>
                <h3 className="font-bold text-gray-800 text-lg mb-2">Excellence</h3>
                <p className="text-gray-600">Committed to delivering exceptional service and results</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">What We Offer</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Comprehensive financial solutions tailored to your needs
            </p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#0077B6] mb-3">Insurance Services</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />Life Insurance</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />Critical Illness Insurance</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />Disability Insurance</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />Travel Insurance</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#0077B6] mb-3">Investment Solutions</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />RRSPs & TFSAs</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />RESPs</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />Segregated Funds</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />Retirement Planning</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#0077B6] mb-3">Mortgage Services</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />Residential Mortgages</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />Commercial Mortgages</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />Refinancing</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2" />Pre-approvals</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Ready to Start Your Financial Journey?</h2>
          <p className="text-blue-100 mb-6 text-lg">
            Connect with one of our expert advisors today
          </p>
          <Button asChild size="lg" className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#003D5C] font-bold text-lg px-8">
            <Link to="/team">Meet Our Team</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
